import pluralize from "pluralize"
import { CaretRight } from "phosphor-react"
import type { ShareProfileRoomFragment } from "~/__generated__/graphql"
import { shareRoomPath } from "~/common/paths"
import { formatCount } from "~/common/number-utils"
import { AppText } from "~/ui/app-text"

export const profileRoomHref = (room: ShareProfileRoomFragment, referrerUsername: string) =>
  `${shareRoomPath({ room_handle: room.roomHandle ?? "" })}?${new URLSearchParams({ u: referrerUsername })}`

const RoomRow = ({ room, href }: { room: ShareProfileRoomFragment; href: string }) => (
  <a
    href={href}
    className="flex flex-row items-center gap-3 rounded-[16px] bg-opacity-black p-3 transition-colors hover:bg-base-black/40 active:bg-base-black/40"
  >
    {room.photoUrl ? (
      <img
        src={room.photoUrl}
        alt=""
        className="aspect-square w-[56px] shrink-0 rounded-[12px] border border-base-white/30 object-cover"
      />
    ) : (
      <div className="flex aspect-square w-[56px] shrink-0 items-center justify-center rounded-[12px] border border-base-white/30">
        <AppText variant="h3" className="text-base-white">
          {room.name.charAt(0)}
        </AppText>
      </div>
    )}
    <div className="flex min-w-0 flex-1 flex-col">
      <AppText variant="body2-medium" className="truncate text-base-white">
        {room.name}
      </AppText>
      {room.description && (
        <AppText variant="body3" className="line-clamp-2 text-base-white/80">
          {room.description}
        </AppText>
      )}
      {room.activeRoomFollowsCount > 0 && (
        <AppText variant="caption" className="text-base-white/60">
          {formatCount(room.activeRoomFollowsCount)}{" "}
          {pluralize("member", room.activeRoomFollowsCount)}
        </AppText>
      )}
    </div>
    <CaretRight size={20} weight="bold" className="shrink-0 text-base-white/60" aria-hidden />
  </a>
)

export const ProfileRoomsSection = ({
  title,
  rooms,
  referrerUsername,
}: {
  title: string
  rooms: ShareProfileRoomFragment[]
  referrerUsername: string
}) =>
  rooms.length === 0 ? null : (
    <section className="flex flex-col gap-2">
      <AppText as="h2" variant="hairline-2" className="px-1 text-base-white">
        {title}
      </AppText>
      {rooms.map((room) => (
        <RoomRow key={room.id} room={room} href={profileRoomHref(room, referrerUsername)} />
      ))}
    </section>
  )
